import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';

import type { ThreadFilters } from '@/api/chat-client';
import { AdminConversationsScreen } from '@/routes/admin/communication/admin-conversations-screen';

// =============================================================================
// admin-conversations-route.tsx — Adaptateur routeur ADMIN « Conversations » (R15-COM-02).
// =============================================================================
// L'ÉTAT DE VUE (recherche SIRET `q`, campagne, non-lus, fil ouvert `thread`) est porté par l'URL via
// TanStack Router : partageable, compatible bouton retour, aucun état métier en localStorage (RULE_REF_006).
// Le composant de présentation (AdminConversationsScreen) reste testable hors routeur (props d'état).

/** Forme validée du search de la route conversations admin (état d'URL). */
export interface AdminConversationsSearch {
  readonly campaign?: string;
  readonly unread?: boolean;
  readonly q?: string;
  readonly thread?: string;
}

/** Valide/normalise le search (allowlist stricte, défauts non sérialisés → URL propre). */
export function validateAdminConversationsSearch(
  search: Record<string, unknown>,
): AdminConversationsSearch {
  const result: {
    campaign?: string;
    unread?: boolean;
    q?: string;
    thread?: string;
  } = {};
  const campaign = search['campaign'];
  if (typeof campaign === 'string' && campaign.length > 0) {
    result.campaign = campaign;
  }
  if (search['unread'] === true || search['unread'] === 'true') {
    result.unread = true;
  }
  const q = search['q'];
  if (typeof q === 'string' && q.length > 0) {
    result.q = q;
  }
  const thread = search['thread'];
  if (typeof thread === 'string' && thread.length > 0) {
    result.thread = thread;
  }
  return result;
}

export function AdminConversationsRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as AdminConversationsSearch;

  const filters: ThreadFilters = {
    ...(search.campaign !== undefined ? { campaignId: search.campaign } : {}),
    ...(search.unread === true ? { unreadOnly: true } : {}),
    ...(search.q !== undefined ? { q: search.q } : {}),
  };
  const selectedThreadId = search.thread ?? null;

  const onFiltersChange = React.useCallback(
    (next: ThreadFilters): void => {
      void navigate({
        to: '/admin/conversations',
        search: {
          ...(next.campaignId !== undefined
            ? { campaign: next.campaignId }
            : {}),
          ...(next.unreadOnly === true ? { unread: true } : {}),
          ...(next.q !== undefined ? { q: next.q } : {}),
          // Le fil ouvert survit au changement de filtre uniquement s'il reste cohérent : on le conserve.
          ...(selectedThreadId !== null ? { thread: selectedThreadId } : {}),
        },
      });
    },
    [navigate, selectedThreadId],
  );

  const onSelectThread = React.useCallback(
    (threadId: string | null): void => {
      void navigate({
        to: '/admin/conversations',
        search: {
          ...(search.campaign !== undefined ? { campaign: search.campaign } : {}),
          ...(search.unread === true ? { unread: true } : {}),
          ...(search.q !== undefined ? { q: search.q } : {}),
          ...(threadId !== null ? { thread: threadId } : {}),
        },
      });
    },
    [navigate, search.campaign, search.unread, search.q],
  );

  return (
    <AdminConversationsScreen
      filters={filters}
      onFiltersChange={onFiltersChange}
      selectedThreadId={selectedThreadId}
      onSelectThread={onSelectThread}
    />
  );
}
